// Whether this user has already been shown the guided walkthrough.
//
// # Why it is per user, not per browser
//
// Reviewers and auditors share machines. A single global flag means the second
// person to sign in never sees the tour, which is exactly the person most likely
// to need it — and the failure is invisible, because "no tour appeared" looks
// identical to "already seen it".
//
// # Why localStorage rather than the server
//
// It is a UI preference, not a study record: losing it shows someone the tour a
// second time, which is mildly annoying and nothing worse. That does not justify
// a round trip on every mount or a schema change. If it ever needs to follow a
// user across devices it belongs in the profile, not here.

const PREFIX = "coronis.audit.tour.seen";

/**
 * Storage key for a principal.
 *
 * An anonymous or not-yet-loaded user gets no key at all rather than a shared
 * one. Falling back to a shared key would let a signed-out visit mark the tour
 * seen for whoever signs in next.
 */
function keyFor(userId: string | null | undefined): string | null {
  const id = userId?.trim();
  return id ? `${PREFIX}.${id}` : null;
}

/**
 * Reads and writes both swallow storage failures.
 *
 * localStorage throws rather than returning null in Safari private browsing and
 * under some enterprise policies, and jsdom does not provide it at all. The tour
 * is help content — it must never be the reason a reviewer's screen fails to
 * render. Unreadable storage degrades to "not seen", which shows the tour again
 * rather than hiding it: the recoverable direction.
 */
export function hasSeenTour(userId: string | null | undefined): boolean {
  const key = keyFor(userId);
  if (!key) return false;
  try {
    return globalThis.localStorage?.getItem(key) === "1";
  } catch {
    return false;
  }
}

/** Marks the tour seen. A failure here means it shows again; that is acceptable. */
export function markTourSeen(userId: string | null | undefined): void {
  const key = keyFor(userId);
  if (!key) return;
  try {
    globalThis.localStorage?.setItem(key, "1");
  } catch {
    // Ignored on purpose — see above.
  }
}

/** Clears the flag. Exposed for tests and for a "show me this again" affordance. */
export function clearTourSeen(userId: string | null | undefined): void {
  const key = keyFor(userId);
  if (!key) return;
  try {
    globalThis.localStorage?.removeItem(key);
  } catch {
    // Ignored on purpose.
  }
}
